<template>
  <div
    class="layout-container"
    :class="{ 'layout-container--mobile': isMobile }"
  >
    <SideBar />
    <div class="layout-main">
      <!-- Шапка раздела на телефоне живёт в разметке всегда, а не по `v-if`:
           в её блок кнопок (`#mobile-topbar-actions`) целятся телепорты
           разделов — проценки, журнала, наценок, настроек функций. Пока шапка
           появлялась и исчезала вместе с классом устройства, при переключении
           отображения (поворот телефона, переключение в инструментах
           разработчика) телепорт переносил узлы в цель, которой уже нет: Vue
           падал внутри `moveTeleport`, обновление раздела прерывалось на
           середине — и таблица «залипала» с раскладкой от прошлого класса
           устройства, а строки оставались пустыми скелетонами (CAB-607).
           `v-show` держит цель телепортов в DOM постоянно. -->
      <MobileTopBar v-show="isMobile" />
      <div class="content">
        <EplexTrialBanner />
        <router-view v-if="isIdentityResolved" />
      </div>
    </div>
    <GuidanceOverlay />
    <GuidanceFloatingButton />
    <OnboardingChecklistWidget v-if="accountInfoStore.isOnboardingParticipant" />
    <PwaUpdateBanner />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, watch } from "vue";
import SideBar from "../components/SideBar.vue";
import MobileTopBar from "../components/MobileTopBar.vue";
import PwaUpdateBanner from "../components/PwaUpdateBanner.vue";
import GuidanceOverlay from "../components/GuidanceOverlay.vue";
import EplexTrialBanner from "../modules/Eplex/components/EplexTrialBanner.vue";
import GuidanceFloatingButton from "../components/GuidanceFloatingButton.vue";
import OnboardingChecklistWidget from "../components/OnboardingChecklistWidget.vue";
import { useGuidanceStore } from "../store/guidance";
import { usePersonalizationStore } from "../store/eplex/eplexPersonalization";
import { useClientContextStore } from "../store/eplex/eplexClientContext";
import { useEplexAccountInfoStore } from "../store/eplex/eplexAccountInfo";
import { useBreakpoints } from "../common/composables/useBreakpoints";
import { useTouchHints } from "../common/composables/useTouchHints";
import { usePwa } from "../common/composables/usePwa";
import { useSupplierCatalogSync } from "../common/composables/eplex/useSupplierCatalogSync";
import { useMarkupSettingsSync } from "../common/composables/eplex/useMarkupSettingsSync";
import { useOnboardingAccess } from "../common/composables/useOnboardingAccess";
import { useSessionFreshness } from "../common/composables/useSessionFreshness";
import { useSignalWidget } from "../common/composables/useSignalWidget";
import { teardownSignalWidget } from "../common/helpers/signalWidget";

const guidanceStore = useGuidanceStore();
const personalizationStore = usePersonalizationStore();
const clientContextStore = useClientContextStore();
const accountInfoStore = useEplexAccountInfoStore();
const { isMobile } = useBreakpoints();

// Смена клиента или выхода из демо-доступа обязана дойти до кэшей каталога
// поставщиков на любом открытом экране, а не только после F5 (CAB-696)
useSupplierCatalogSync();
// Наценки лежат на сервере и нужны всем разделам: читаем при входе и при смене
// личности здесь, а не в каждом экране (CAB-778)
useMarkupSettingsSync();
// Кабинет держат открытым сутками — сведения о сессии обязаны обновляться и без
// перезагрузки страницы (CAB-734)
useSessionFreshness();
// Квесты, награды и их опрос — только участнику онбординга, пока он им остаётся
useOnboardingAccess();

const signalWidget = useSignalWidget();

/**
 * Страницы монтируем только когда известно, кто вошёл (единая авторизация).
 *
 * На единой авторизации личность приезжает с бэкенда, а не из токена, поэтому в
 * первый момент загрузки её в браузере нет вовсе. По ней собираются ключи
 * сохранённого состояния — вкладки проценки, история поиска, цели подбора,
 * настройки плагинов. Страница, смонтированная раньше контекста, читает состояние
 * ключом без почты: проценка и история выглядят пустыми, а первое же сохранение
 * затирает настоящую запись пустой. Не ждём только недоступный контекст: из-за чата
 * или баннера демо-доступа человека нельзя оставить без кабинета.
 */
const isIdentityResolved = computed(
  () => clientContextStore.status === "ready" || clientContextStore.status === "error",
);

// Подсказки `title` там, где навести курсор нечем — по долгому нажатию
useTouchHints();
// Установка на телефон, офлайн-оболочка и обновление версии
usePwa();

watch(
  () => personalizationStore.theme,
  (newTheme) => {
    window.SignalWidget?.setTheme(newTheme);
  },
);

// Виджет сам предлагает включить пуш-уведомления сразу при монтировании
// (ТЕСТ ОНБОРДИНГ 2, п.8) — пока идёт онбординг, он не грузится, чтобы не
// спорить с туром за внимание. Исключение — «Настройте за меня»: там виджет
// поднимается по требованию без вводного окна (useConfigureForMe), а после
// завершения онбординга ensureMounted() снимает это подавление.
//
// watch() вызывается уже после await внутри onMounted — Vue не привязывает
// такой эффект к экземпляру автоматически, поэтому останавливаем его вручную
// в onUnmounted, а не полагаемся на авто-остановку при размонтировании.
let stopOnboardingWatch: (() => void) | null = null;

function loadSignalWidgetAfterOnboarding(): void {
  if (guidanceStore.activeKind !== "onboarding") {
    signalWidget.ensureMounted();
    return;
  }

  stopOnboardingWatch = watch(
    () => guidanceStore.activeKind,
    (kind) => {
      if (kind === "onboarding") return;
      stopOnboardingWatch?.();
      stopOnboardingWatch = null;
      signalWidget.ensureMounted();
    },
  );
}

onMounted(async () => {
  personalizationStore.init();
  accountInfoStore.load();
  // guidanceStore.init() решает, какой тур активен (activeKind), по isTrial —
  // обязательно дождаться контекста клиента, иначе новый демо-клиент на долю
  // секунды получит тур работы вместо онбординга (гонка), а ничего не
  // переисчитывает уже показанный экран, когда isTrial наконец приходит.
  await clientContextStore.load();
  guidanceStore.init();
  loadSignalWidgetAfterOnboarding();
});

onUnmounted(() => {
  teardownSignalWidget();
  stopOnboardingWatch?.();
});
</script>
